import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { init, dispose, ActionType, CandleType } from 'klinecharts'
import type { Chart, KLineData, VisibleRange } from 'klinecharts'
import { buildStyles, defaultBackground, EMPTY_TOOLTIP } from '@/components/Workbench/chartStyles'
import { registerVolumeOverlay, VOLUME_INDICATOR, volColors } from '@/components/Workbench/volumeOverlay'
import { useIsDark } from '@/components/Workbench/useIsDark'
import { getCandleCountForTimeframe, getTimeframeSeconds } from '../../utils/chartUtils'
import { fetchSymbolMeta, precisionFromPointSize } from '../adapters/symbolsFetcher'
import type { CandleData, OrderData } from '../types/chartData'
import type { PairKey } from '../utils/orderPairing'
import { registerBacktestChartOverlays } from './backtestChartOverlays'
import { findBarIndex, OrderLayer } from './replayOrderLayer'
import {
  closedIndexRange,
  getIndexRange,
  getVisibleBarCount as engineVisibleBarCount,
  setIndexRange,
  setPanBounds,
  rightPanBoundPx,
  MIN_RIGHT_PAN_BOUND,
  type IndexRange,
} from './klineViewport'
import type { FormingBarState } from '../utils/formingBarBuffer'
import { ChartGapHandle } from '@/components/ChartGapHandle/ChartGapHandle'
import BacktestChartLegend from './BacktestChartLegend'
import BacktestOrderTooltip from './BacktestOrderTooltip'

/**
 * The Backtest tab's price chart, on klinecharts + the datafeed WebSocket (the
 * same engine and transport as the Workbench).
 *
 * The chart is a dumb mirror of the `candles` prop: replay reveals bars by
 * shortening that array upstream (useBacktestChartState slices at the reveal
 * boundary), and the viewport is driven from outside through the imperative
 * handle. This component owns no data loading and no replay state.
 *
 * `candles` / `orders` carry unix SECONDS; klinecharts wants milliseconds, and
 * this file is the only place that conversion happens.
 */

const DEFAULT_PRECISION = 5

export interface BacktestChartHandle {
  setVisibleLogicalRange: (range: IndexRange) => void
  getVisibleLogicalRange: () => IndexRange | null
  getCandleCount: () => number
  /**
   * How many bars fit on screen at the current zoom.
   *
   * Deliberately NOT `getVisibleLogicalRange().to - .from`, which is a bar short whenever the
   * range is clamped — at the live edge, and under a forming bar. A caller that re-applies a
   * span on every replay tick needs the exact one. See klineViewport.getVisibleBarCount.
   */
  getVisibleBarCount: () => number | null
  /**
   * Paint (or clear) the bar currently forming just right of the last closed one.
   *
   * Driven imperatively at ~30fps by useFormingBar during tick replay, so React
   * never re-renders per frame. The forming bar lives only in the engine — it is
   * NOT part of `candles`, and `getCandleCount()` keeps reporting closed bars
   * only. Passing null removes it.
   *
   * `revealTs` is the sub-bar cursor, in unix seconds. Given it, trade markers
   * and connectors are reconciled at that instant, so a fill part-way through the
   * bar shows its arrow before the bar closes. Omit it to leave overlays at bar
   * granularity. `barProgress` is how much of the bar has played, 0..1.
   */
  setFormingBar: (candle: FormingBarState | null, revealTs?: number, barProgress?: number) => void
}

interface BacktestChartProps {
  candles: CandleData[]
  orders?: OrderData[]
  symbol: string
  timeframe: string
  /** Reveal ceiling while a backtest is still running (unix seconds). */
  maxAvailableTimestamp?: number
  /** Fit the newest bars on first paint. Off when a parent owns the viewport. */
  autoSetInitialVisibleRange?: boolean
  onVisibleRangeChange?: (range: IndexRange) => void
  onUserNavigate?: () => void
  /**
   * Hands the raw klinecharts instance up (null on dispose), mirroring
   * WorkbenchChart.onReady.
   *
   * The imperative handle above covers everything replay needs, but a caller that wants to
   * paint its OWN overlays on this chart needs the engine itself. The walkthrough does: it
   * repaints the user's saved drawings alongside the order markers.
   */
  onChartReady?: (chart: Chart | null) => void
  /** Draw the volume overlay. Off for callers that want a bare price chart. */
  showVolume?: boolean
  /**
   * Stop panning at the loaded data instead of letting the user drag off into blank
   * space (klinecharts allows ~visibleBarCount - 2 bar widths past the newest bar).
   *
   * Opt-in, not the default: the Backtest tab has always panned this way, and the
   * walkthrough is the case where it actively misleads — the user is validating one
   * specific drawn range, so scrolling past its end shows nothing but the last bar's
   * price line stretched over emptiness.
   */
  boundPanToData?: boolean
  /**
   * Bar widths of blank space held right of the newest bar, whenever the view is pinned to
   * the live edge.
   *
   * For a replay: with no margin the bar being revealed appears hard against the price axis,
   * which is the one place on the chart the eye is least able to read it. A few bars of run-off
   * give each new candle somewhere to land. Costs the same number of bars off the left of the
   * window — the span is not re-zoomed to make room.
   *
   * Read once at init for the pan bound it implies, like `boundPanToData` — pass a constant.
   */
  rightPadBars?: number
  /**
   * Hold the zoom at this bar width, in px, and let only the user change it. Off by default.
   *
   * Without it the chart zooms whenever it is framed: every range the replay asks for — each play
   * tick on the Backtest tab, each step, scrub, trade row and window reload everywhere — is sized
   * to the timeframe's default screen, and setIndexRange zooms to fit it. With it, a framing is a
   * SCROLL (see setIndexRange's `keepZoom`): the range says where to look, never how close. The
   * engine's own bar space is the one source of truth after init, and only a zoom gesture — wheel,
   * pinch, a drag on the time axis — moves it; `onBarSpaceChange` reports each one.
   *
   * Read once at init, like `rightPadBars` — pass a constant.
   */
  fixedBarSpace?: number
  /** The user zoomed: the bar width they chose, in px. Only fires for gestures, never for framing. */
  onBarSpaceChange?: (barSpace: number) => void
  /**
   * The walkthrough's chart rules. Opt-in, so the Backtest tab is unaffected.
   *
   * Two rules, both about not telling the trader more than the moment knows:
   *  - a position's entry / stop-loss / take-profit are drawn as working lines while it is
   *    OPEN, and disappear when it closes (the arrows and their connector take over);
   *  - an entry arrow stays neutral grey until its position closes, then flips to
   *    win / loss — so watching a replay does not reveal the outcome at the entry.
   *
   * Also lights one trade's lines and dims the others on hover, while paused.
   */
  liveOrderLines?: boolean
  /** From backtest_config.pip_size. Puts a pip distance on each level's label. */
  pipSize?: number | null
  /**
   * entry time -> ticket number, for naming orders in the click tooltip.
   *
   * Passed in rather than counted here: this chart only holds the orders for the candle window
   * it has paged in, so anything it counted for itself would renumber on a pan and disagree
   * with the trades pane. The walkthrough supplies it (useWalkthroughOrders); the Backtest tab
   * has no ticket numbers and omits it, leaving the tooltip exactly as it was.
   */
  /** Display number per pairing key (`ticketsByKey`), for the tooltip. */
  orderTickets?: ReadonlyMap<PairKey, number>
  /** The space after the newest bar the user set, px; null or absent keeps `rightPadBars`. */
  rightGap?: number | null
  /** Shows the chevron that drags that space, and reports each move. Off when absent. */
  onRightGapChange?: (px: number) => void
}

function toKline(c: CandleData): KLineData {
  return {
    timestamp: c.time * 1000,
    open: c.open,
    high: c.high,
    low: c.low,
    close: c.close,
    volume: c.volume,
  }
}

/**
 * klinecharts prints a dev-only "Welcome to klinecharts" banner synchronously
 * inside init() with no option to disable it — mute console.log for just that
 * one line while init() runs.
 */
function initQuietly(el: HTMLElement): Chart | null {
  const nativeLog = console.log
  console.log = (...args: unknown[]) => {
    if (typeof args[0] === 'string' && args[0].includes('Welcome to klinecharts')) return
    nativeLog(...args)
  }
  try {
    return init(el)
  } finally {
    console.log = nativeLog
  }
}

/**
 * How far past either end of the data `boundPanToData` still lets the user pan.
 *
 * A sliver — ~3 bars at the default bar space, versus the ~48 the engine allows by
 * default — and not a px less than MIN_RIGHT_PAN_BOUND, or the same cap that stops the
 * user would also clip the forming bar's half-bar margin at high zoom.
 */
const PAN_MARGIN_PX = MIN_RIGHT_PAN_BOUND

/** Right pan bound, at least the user's gap: the engine caps the offset at it. */
function rightBoundPx(rightPadBars: number, gap: number | null): number {
  return Math.max(rightPanBoundPx(rightPadBars), gap ?? 0)
}

const BacktestChart = forwardRef<BacktestChartHandle, BacktestChartProps>(function BacktestChart(
  {
    candles,
    orders = [],
    symbol,
    timeframe,
    maxAvailableTimestamp,
    autoSetInitialVisibleRange = true,
    onVisibleRangeChange,
    onUserNavigate,
    onChartReady,
    showVolume = true,
    boundPanToData = false,
    rightPadBars = 0,
    fixedBarSpace,
    onBarSpaceChange,
    liveOrderLines = false,
    pipSize = null,
    orderTickets,
    rightGap = null,
    onRightGapChange,
  },
  ref,
) {
  const isDark = useIsDark()
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<Chart | null>(null)
  const [chart, setChart] = useState<Chart | null>(null)
  const [pricePrecision, setPricePrecision] = useState<number | null>(null)
  // Bumped on every data application so the legend re-reads the engine's list.
  const [dataNonce, setDataNonce] = useState(0)

  const onVisibleRangeChangeRef = useRef(onVisibleRangeChange)
  onVisibleRangeChangeRef.current = onVisibleRangeChange
  const onUserNavigateRef = useRef(onUserNavigate)
  onUserNavigateRef.current = onUserNavigate
  const timeframeRef = useRef(timeframe)
  timeframeRef.current = timeframe
  // Read inside the mount-once init effect, so it must not be a dependency.
  const showVolumeRef = useRef(showVolume)
  showVolumeRef.current = showVolume
  const boundPanToDataRef = useRef(boundPanToData)
  boundPanToDataRef.current = boundPanToData
  const rightPadBarsRef = useRef(rightPadBars)
  rightPadBarsRef.current = rightPadBars
  const fixedBarSpaceRef = useRef(fixedBarSpace)
  fixedBarSpaceRef.current = fixedBarSpace
  const onBarSpaceChangeRef = useRef(onBarSpaceChange)
  onBarSpaceChangeRef.current = onBarSpaceChange
  // The gap applied to this chart: the user's, fitted to its width by the handle.
  const userGapRef = useRef(rightGap)

  /** setIndexRange at this chart's margin, holding the user's gap at the live edge. */
  const frameRange = useCallback((c: Chart, range: IndexRange, dataSize: number) => {
    setIndexRange(c, range, dataSize, rightPadBarsRef.current, { keepZoom: fixedBarSpaceRef.current != null })
    const gap = userGapRef.current
    if (gap != null && Math.round(range.to) >= dataSize - 1) c.setOffsetRightDistance(gap)
  }, [])

  const applyUserGap = useCallback((px: number) => {
    const c = chartRef.current
    if (!c) return
    userGapRef.current = px
    if (boundPanToDataRef.current) setPanBounds(c, PAN_MARGIN_PX, rightBoundPx(rightPadBarsRef.current, px))
    c.setOffsetRightDistance(px)
  }, [])

  const dragStartXRef = useRef<number | null>(null)
  const dragNavigatedRef = useRef(false)
  /** Where a single-finger touch began, for the tap-vs-pan test in handleTouchEnd. */
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const initialRangeSetRef = useRef(false)
  // Last-applied series shape, for strict-suffix-append detection.
  const snapshotRef = useRef<{ firstTime: number; lastTime: number; count: number } | null>(null)
  // The trade overlays (markers, connectors, live order lines), diffed per data change — see
  // replayOrderLayer.ts, which the walkthrough's canvas shares.
  const orderLayerRef = useRef<OrderLayer | null>(null)
  // Bar time (unix s) of the forming bar currently in the engine, or null.
  const formingBarRef = useRef<number | null>(null)
  // klinecharts fires OnVisibleRangeChange synchronously from inside updateData
  // — even for an in-place overwrite of the last bar. Left unsuppressed, every
  // animation frame would look like a user pan to useGraphCandleWindow and kick
  // off an extendWindow('right') fetch. The action is synchronous, so a plain
  // flag around the call is enough.
  const formingSuppressRef = useRef(false)
  // Notified after each forming-bar repaint. The engine gives no usable signal
  // of its own here — updateData's recalculateCrosshair reuses the existing
  // crosshair object, so OnCrosshairChange never fires — and bumping React state
  // would re-render the whole chart 30x a second. This lets the legend alone
  // follow the candle as it builds.
  const repaintListenersRef = useRef<Set<() => void>>(new Set())

  const [tooltip, setTooltip] = useState<{
    orders: OrderData[]
    point: { x: number; y: number }
  } | null>(null)

  const effectivePrecision = pricePrecision ?? DEFAULT_PRECISION
  const precisionRef = useRef(effectivePrecision)
  precisionRef.current = effectivePrecision

  // Reveal ceiling for a still-running backtest: drop bars the engine hasn't
  // produced yet. The parent applies the same clip; this is the last guard.
  const displayCandles = useMemo(() => {
    if (maxAvailableTimestamp == null || candles.length === 0) return candles
    const idx = findBarIndex(candles, maxAvailableTimestamp)
    return idx === candles.length - 1 ? candles : candles.slice(0, idx + 1)
  }, [candles, maxAvailableTimestamp])

  const displayCandlesRef = useRef(displayCandles)
  displayCandlesRef.current = displayCandles

  const ordersRef = useRef(orders)
  ordersRef.current = orders
  const liveOrderLinesRef = useRef(liveOrderLines)
  liveOrderLinesRef.current = liveOrderLines
  const pipSizeRef = useRef(pipSize)
  pipSizeRef.current = pipSize

  const hideTooltip = useCallback(() => setTooltip(null), [])

  const subscribeRepaint = useCallback((listener: () => void) => {
    const listeners = repaintListenersRef.current
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  // ---- Init / dispose -------------------------------------------------------
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    registerVolumeOverlay() // no-op after first call
    registerBacktestChartOverlays()

    const instance = initQuietly(el)
    chartRef.current = instance
    if (!instance) return undefined

    // Before any data: these set limits without re-adjusting, so a view that is
    // already overscrolled would stay that way.
    if (boundPanToDataRef.current) {
      // The right bound caps our own live-edge writes as well as the user's drags, so a
      // chart that reserves `rightPadBars` of margin has to leave room for it.
      setPanBounds(instance, PAN_MARGIN_PX, rightBoundPx(rightPadBarsRef.current, userGapRef.current))
    }
    // The engine frames new data at the last offset written.
    if (userGapRef.current != null) instance.setOffsetRightDistance(userGapRef.current)
    // Before any data too, so the first frame is already at the chosen zoom.
    if (fixedBarSpaceRef.current != null) instance.setBarSpace(fixedBarSpaceRef.current)

    // TradingView-style volume: drawn in pixel space at the bottom of the price
    // pane, so it needs no extra pane and never squashes the candles.
    // EMPTY_TOOLTIP suppresses the engine's canvas legend, which would
    // otherwise paint "VOL_OVERLAY" on top of our HTML one.
    if (showVolumeRef.current) {
      instance.createIndicator(
        {
          name: VOLUME_INDICATOR,
          extendData: volColors(false),
          createTooltipDataSource: EMPTY_TOOLTIP,
        },
        true,
        { id: 'candle_pane' },
      )
    }

    // No setLoadDataCallback: history paging is owned by useGraphCandleWindow.
    // Every applyNewData passes `more: false`, which also stops the engine from
    // asking. Two paginators would fight over the same window.

    const onRange = (data?: unknown) => {
      // A forming-bar repaint is not navigation: swallow it before it closes the
      // tooltip or reaches the parent's pan/paging logic.
      if (formingSuppressRef.current) return
      hideTooltip()
      const vr = data as VisibleRange | undefined
      if (!vr || !onVisibleRangeChangeRef.current) return
      // The engine's `to` is exclusive; the parent works in inclusive indices.
      onVisibleRangeChangeRef.current({ from: vr.from, to: Math.max(vr.from, vr.to - 1) })
    }
    instance.subscribeAction(ActionType.OnVisibleRangeChange, onRange)

    // Executed only by the engine's own zoom (wheel, pinch, time-axis drag) — never by a
    // setBarSpace — so every call is the user choosing a bar width.
    const onZoom = () => onBarSpaceChangeRef.current?.(instance.getBarSpace())
    instance.subscribeAction(ActionType.OnZoom, onZoom)

    const ro = new ResizeObserver(() => chartRef.current?.resize())
    ro.observe(el)

    const orderLayer = new OrderLayer(instance)
    orderLayerRef.current = orderLayer

    setChart(instance)

    return () => {
      ro.disconnect()
      instance.unsubscribeAction(ActionType.OnVisibleRangeChange, onRange)
      instance.unsubscribeAction(ActionType.OnZoom, onZoom)
      setChart(null)
      dispose(el)
      chartRef.current = null
      orderLayer.forget()
      orderLayerRef.current = null
      snapshotRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---- Raw engine hand-off -------------------------------------------------
  // Driven off the `chart` STATE rather than called inside the init effect, so a consumer
  // that paints its own overlays gets a reactive signal and a matching null on dispose.
  const onChartReadyRef = useRef(onChartReady)
  onChartReadyRef.current = onChartReady
  useEffect(() => {
    onChartReadyRef.current?.(chart)
    return () => onChartReadyRef.current?.(null)
  }, [chart])

  // ---- Theme ---------------------------------------------------------------
  useEffect(() => {
    const c = chartRef.current
    if (!c) return
    c.setStyles(buildStyles(isDark, {}, CandleType.CandleSolid))
    if (showVolume) {
      c.overrideIndicator({ name: VOLUME_INDICATOR, extendData: volColors(isDark) }, 'candle_pane')
    }
  }, [isDark, chart, showVolume])

  // ---- Price precision from the symbol's point size ------------------------
  // The old chart hardcoded 5 decimals / minMove 0.00001, which is wrong for
  // metals, indices and crypto. Also drives klinecharts' y-axis autoscale
  // padding, so a coarse value squashes quiet windows into a thin strip.
  useEffect(() => {
    let cancelled = false
    setPricePrecision(null)
    fetchSymbolMeta(symbol)
      .then((meta) => {
        if (cancelled) return
        setPricePrecision(precisionFromPointSize(meta?.point_size))
      })
      .catch(() => {
        /* fall back to DEFAULT_PRECISION */
      })
    return () => {
      cancelled = true
    }
  }, [symbol])

  useEffect(() => {
    chartRef.current?.setPriceVolumePrecision(effectivePrecision, 0)
  }, [effectivePrecision, chart])

  // ---- Data ----------------------------------------------------------------
  // Strict suffix-append -> updateData per bar, which is the replay steady
  // state; anything else -> applyNewData. Re-uploading the whole array on every
  // replay tick was measurably too slow, hence the diff.
  //
  // `force` skips the fast path. Clearing a forming bar needs it: the engine has
  // no way to shrink its data list, so dropping that last element means
  // re-applying the closed array wholesale.
  const applyClosedData = useCallback(
    (force = false) => {
      const c = chartRef.current
      if (!c) return

      // Every branch below either overwrites or discards the forming bar.
      formingBarRef.current = null

      if (displayCandles.length === 0) {
        c.applyNewData([], false)
        snapshotRef.current = null
        orderLayerRef.current?.forget()
        setDataNonce((n) => n + 1)
        return
      }

      const snapshot = snapshotRef.current
      const firstTime = displayCandles[0].time
      const lastTime = displayCandles[displayCandles.length - 1].time

      const canStrictAppend =
        !force &&
        snapshot !== null &&
        snapshot.firstTime === firstTime &&
        displayCandles.length > snapshot.count &&
        displayCandles[snapshot.count].time > snapshot.lastTime

      if (canStrictAppend) {
        // When a forming bar is painted, the first of these carries its exact
        // timestamp and so overwrites it in place with the real closed bar —
        // which is why the reveal never visibly jumps.
        for (let i = snapshot!.count; i < displayCandles.length; i++) {
          c.updateData(toKline(displayCandles[i]))
        }
      } else {
        // Unlike lightweight-charts' setData, applyNewData resets the scroll
        // offset to the live edge (resetOffsetRightDistance). Hold the current
        // index window instead; the parent then shifts/clamps it as it always did.
        const prev = closedIndexRange(c, displayCandles.length)
        c.applyNewData(displayCandles.map(toKline), false)
        if (prev) {
          const maxIdx = displayCandles.length - 1
          const span = Math.min(prev.to - prev.from, maxIdx)
          const to = Math.min(prev.to, maxIdx)
          frameRange(c, { from: Math.max(0, to - span), to }, displayCandles.length)
        }
      }

      snapshotRef.current = { firstTime, lastTime, count: displayCandles.length }
      setDataNonce((n) => n + 1)
    },
    [displayCandles, frameRange],
  )

  const applyClosedDataRef = useRef(applyClosedData)
  applyClosedDataRef.current = applyClosedData

  // useLayoutEffect so this lands before the parent's viewport layout effect
  // (child effects run first) and before paint.
  useLayoutEffect(() => {
    applyClosedData()

    const c = chartRef.current
    if (!c || displayCandles.length === 0) return
    if (autoSetInitialVisibleRange && !initialRangeSetRef.current) {
      initialRangeSetRef.current = true
      const visibleCount = getCandleCountForTimeframe(timeframe, displayCandles.length)
      const to = displayCandles.length - 1
      frameRange(c, { from: Math.max(0, to - visibleCount + 1), to }, displayCandles.length)
    }
  }, [applyClosedData, displayCandles, timeframe, autoSetInitialVisibleRange, chart, frameRange])

  useImperativeHandle(
    ref,
    () => ({
      setVisibleLogicalRange(range: IndexRange) {
        const c = chartRef.current
        if (!c) return
        // Deliberately the CLOSED count, even while a forming bar is painted:
        // the half-bar right margin that produces is exactly the gap the forming
        // bar occupies. Passing the engine's count instead would hide it.
        const n = displayCandlesRef.current.length
        const keepZoom = fixedBarSpaceRef.current != null
        let next = range
        // At a fixed zoom a range only says WHERE to look. At the live edge that is its newest
        // bar, which is `to` as given. Anywhere else it is the range's centre: a trade row asks for
        // a window centred on the trade, sized to a screen this chart is not holding, so keeping
        // its `to` would slide the trade off to one side.
        if (keepZoom && range.to < n - 1) {
          const v = engineVisibleBarCount(c)
          if (v != null) {
            const to = Math.round((range.from + range.to) / 2 + v / 2)
            next = { from: to - v, to }
          }
        }
        frameRange(c, next, n)
      },
      getVisibleLogicalRange() {
        const c = chartRef.current
        return c ? closedIndexRange(c, displayCandlesRef.current.length) : null
      },
      getCandleCount() {
        return displayCandlesRef.current.length
      },
      getVisibleBarCount() {
        const c = chartRef.current
        return c ? engineVisibleBarCount(c) : null
      },
      setFormingBar(candle: FormingBarState | null, revealTs?: number) {
        const c = chartRef.current
        if (!c) return

        if (candle === null) {
          if (formingBarRef.current === null) return
          // applyClosedData bumps dataNonce, which re-reconciles overlays back to
          // bar granularity — so intra-bar arrows are dropped with the bar.
          applyClosedDataRef.current(true)
          return
        }

        const closedCount = displayCandlesRef.current.length
        if (closedCount === 0) return

        // Decide append-vs-overwrite against the engine's live last bar rather
        // than our own bookkeeping, so an applyNewData that wiped the forming bar
        // (timeframe change, window reload, prune) self-heals on the next frame.
        const list = c.getDataList()
        const engineLastMs = list.length > 0 ? list[list.length - 1].timestamp : null
        const candleMs = candle.time * 1000
        // Older than the engine's last bar is a silent no-op inside updateData.
        // Skip rather than pretend it painted; the next frame revalidates.
        if (engineLastMs !== null && candleMs < engineLastMs) return

        formingSuppressRef.current = true
        try {
          c.updateData(toKline(candle))

          // Keep the forming bar on screen. In the replay steady state the right
          // margin is already half a bar wide and this is a no-op. After a head
          // prune or a window reload that margin is gone and the appended bar
          // lands off-screen — so nudge one bar right, but only when the view is
          // pinned to the last closed bar. If the user has scrolled back into
          // history, leave their view alone.
          const vr = getIndexRange(c)
          if (vr && vr.to === closedCount - 1) {
            const span = vr.to - vr.from
            frameRange(c, { from: Math.max(0, closedCount - span), to: closedCount }, closedCount + 1)
          }
        } finally {
          formingSuppressRef.current = false
        }

        formingBarRef.current = candle.time

        // After updateData, so the forming bar is already in the engine's data
        // list and an overlay anchored to its timestamp resolves to it rather
        // than to the last closed bar.
        if (revealTs !== undefined) reconcileOverlaysRef.current(revealTs, candle)

        for (const listener of repaintListenersRef.current) listener()
      },
    }),
    [frameRange],
  )

  // ---- Trade markers + entry→exit connectors -------------------------------
  // ONE implementation, two callers: the data effect below (whole revealed set)
  // and setFormingBar (the sub-bar cursor). The layer computes the full desired
  // key set and diffs create/remove, so calling it from two places converges with
  // no orphaned or duplicated overlays. A second, independent reveal path writing
  // the same overlays is exactly how those bugs happen.
  const reconcileOverlays = useCallback(
    (revealTs: number, forming: FormingBarState | null) => {
      orderLayerRef.current?.reconcile({
        bars: displayCandlesRef.current,
        orders: ordersRef.current,
        timeframe: timeframeRef.current,
        forming,
        revealTs,
        liveOrderLines: liveOrderLinesRef.current,
        pipSize: pipSizeRef.current,
        precision: precisionRef.current,
      })
    },
    [],
  )

  const reconcileOverlaysRef = useRef(reconcileOverlays)
  reconcileOverlaysRef.current = reconcileOverlays

  useEffect(() => {
    reconcileOverlays(Infinity, null)
    // displayCandles/orders/timeframe/liveOrderLines/pipSize are read through refs, but they
    // are what makes the result change, so they stay in the dep list.
  }, [reconcileOverlays, displayCandles, orders, timeframe, liveOrderLines, pipSize, dataNonce, chart])

  // ---- Interactions --------------------------------------------------------
  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    dragStartXRef.current = event.clientX
    dragNavigatedRef.current = false
  }, [])

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (dragStartXRef.current === null || dragNavigatedRef.current) return
      if (Math.abs(event.clientX - dragStartXRef.current) < 8) return
      dragNavigatedRef.current = true
      hideTooltip()
      onUserNavigateRef.current?.()
    },
    [hideTooltip],
  )

  const clearPointerDrag = useCallback(() => {
    dragStartXRef.current = null
    dragNavigatedRef.current = false
  }, [])

  const handleTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    hideTooltip()
    // Where the finger landed, so touchend can tell a tap from a pan. Multi-touch is a pinch,
    // never a tap, so it records nothing and the tap check below fails closed.
    const t = event.touches.length === 1 ? event.touches[0] : null
    touchStartRef.current = t ? { x: t.clientX, y: t.clientY } : null
    onUserNavigateRef.current?.()
  }, [hideTooltip])

  // Every wheel gesture counts as navigation now that the engine zooms on
  // vertical wheel (lightweight-charts had handleScale.mouseWheel disabled, so
  // only horizontal/shift wheel used to mean anything).
  const handleWheel = useCallback(() => {
    hideTooltip()
    onUserNavigateRef.current?.()
  }, [hideTooltip])

  /** Open the order card for the bar under a point, in container pixels. */
  const openOrderCardAt = useCallback((x: number, y: number) => {
    const c = chartRef.current
    if (!c) return
    const res = c.convertFromPixel([{ x, y }], { paneId: 'candle_pane', absolute: true })
    const point = Array.isArray(res) ? res[0] : res
    const dataIndex = point?.dataIndex

    const bars = displayCandlesRef.current
    if (dataIndex == null || dataIndex < 0 || dataIndex >= bars.length) {
      setTooltip(null)
      return
    }

    // Orders on the clicked bar: [barTime, barTime + timeframe).
    const barTime = bars[dataIndex].time
    const barEnd = barTime + getTimeframeSeconds(timeframeRef.current)
    const matched = ordersRef.current.filter((o) => o.time >= barTime && o.time < barEnd)
    setTooltip(matched.length > 0 ? { orders: matched, point: { x, y } } : null)
  }, [])

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const el = containerRef.current
      if (!el || dragNavigatedRef.current) return
      const rect = el.getBoundingClientRect()
      openOrderCardAt(event.clientX - rect.left, event.clientY - rect.top)
    },
    [openOrderCardAt],
  )

  /**
   * A TAP HAS TO OPEN THE CARD ITSELF, because no click ever arrives on a touch device.
   *
   * klinecharts calls preventDefault() on touchend whenever it has a tap handler of its own —
   * which is always (SyntheticEvent._touchEndHandler: "do not fire mouse events if tap handler
   * was executed") — and that suppresses the compatibility click the browser would otherwise
   * synthesize. So handleClick above is a mouse-only route, and the phone was left with markers
   * it could see and not open.
   */
  const handleTouchEnd = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      const start = touchStartRef.current
      touchStartRef.current = null
      // One finger, all of it lifted, and it barely moved. Anything else is a pan or a pinch,
      // and the chart has already treated it as navigation.
      if (!start || event.touches.length > 0 || event.changedTouches.length !== 1) return
      const t = event.changedTouches[0]
      // Same 8px the pointer drag check uses, as a manhattan distance — klinecharts cancels its
      // own tap on a comparable threshold, so the two agree about what a tap is.
      if (Math.abs(t.clientX - start.x) + Math.abs(t.clientY - start.y) > 8) return
      const el = containerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      openOrderCardAt(t.clientX - rect.left, t.clientY - rect.top)
    },
    [openOrderCardAt],
  )

  return (
    <div
      data-testid="backtest-chart"
      className="relative h-full w-full"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={clearPointerDrag}
      onPointerCancel={clearPointerDrag}
      onPointerLeave={clearPointerDrag}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
      onClick={handleClick}
    >
      <div
        ref={containerRef}
        className="absolute inset-0"
        style={{ backgroundColor: defaultBackground(isDark) }}
      />
      {onRightGapChange && chart && (
        <ChartGapHandle
          chart={chart}
          gap={rightGap}
          revision={dataNonce}
          onChange={onRightGapChange}
          onApply={applyUserGap}
        />
      )}
      {chart && (
        <BacktestChartLegend
          chart={chart}
          precision={effectivePrecision}
          dataNonce={dataNonce}
          subscribeRepaint={subscribeRepaint}
        />
      )}
      {tooltip && containerRef.current && (
        <BacktestOrderTooltip
          orders={tooltip.orders}
          point={tooltip.point}
          containerWidth={containerRef.current.clientWidth}
          precision={effectivePrecision}
          tickets={orderTickets}
        />
      )}
    </div>
  )
})

export default BacktestChart
